/**
 * Distribuição da carteira do cliente por classe de ativo.
 *
 * ⚠️ A MESMA conta existe hoje dentro do `profile-investimentos.vue`, na página
 * `/cliente-perfil`. Ela NÃO foi extraída de lá: aquele componente é de outra
 * frente e não se mexe por causa desta. A consequência é conhecida e aceita —
 * um ajuste na regra precisa ser feito nos dois lugares, e o dia em que as duas
 * telas discordarem começa aqui. Unificar é conversa para quando as duas
 * frentes se encontrarem.
 *
 * ⚠️ Devolve só a MATEMÁTICA (`label` + `valor` em %). A cor fica com quem
 * desenha — aqui, os `TONS` do módulo de IA.
 */

// Chaves do ativo como o `ws/clientAssets` as entrega. `CI` é a classe do ativo
// e `Aplicação` o valor aplicado — nomes do backend, com acento e tudo.
const KEY_CLASSE = 'CI'
const KEY_APLICACAO = 'Aplicação'
// Ativo sem classe não some da conta: ele compõe o patrimônio, e descartá-lo
// faria os percentuais fecharem sobre um total menor que o real.
const CLASSE_FALLBACK = 'Sem classe'
const ZERO = 0
const PERCENT_FACTOR = 100

/**
 * @param {Array<Object>} ativos ativos JÁ filtrados pelo cliente
 * @returns {Array<{label: string, valor: number}>} percentuais, maior primeiro
 */
export const alocacaoPorClasse = (ativos) => {
  const somaPorClasse = {}
  let total = ZERO

  ;(ativos || []).forEach((ativo) => {
    const classe = ativo && ativo[KEY_CLASSE] ? ativo[KEY_CLASSE] : CLASSE_FALLBACK
    const aplicacao = ativo && ativo[KEY_APLICACAO] ? ativo[KEY_APLICACAO] : ZERO
    somaPorClasse[classe] = (somaPorClasse[classe] || ZERO) + aplicacao
    total += aplicacao
  })

  // Total zero é carteira sem cotização, não carteira vazia de erro. Lista vazia
  // deixa quem desenha escolher o texto — dividir por zero devolveria `NaN` em
  // toda fatia, e `NaN%` na tela lê como defeito.
  if (!total) return []

  return Object.keys(somaPorClasse)
    .filter((classe) => somaPorClasse[classe] > ZERO)
    .map((classe) => ({
      label: classe,
      valor: (somaPorClasse[classe] / total) * PERCENT_FACTOR,
    }))
    .sort((a, b) => b.valor - a.valor)
}

/**
 * Soma aplicada da carteira — o "R$ 14,8M" do topo do painel.
 *
 * Sai daqui e não de outra chamada porque é a MESMA base dos percentuais: se o
 * total viesse de outra fonte, o cabeçalho e as fatias poderiam discordar.
 *
 * @param {Array<Object>} ativos ativos JÁ filtrados pelo cliente
 * @returns {number}
 */
export const totalAplicado = (ativos) =>
  (ativos || []).reduce((soma, ativo) => {
    const aplicacao = ativo && ativo[KEY_APLICACAO] ? ativo[KEY_APLICACAO] : ZERO
    return soma + aplicacao
  }, ZERO)
